import { Card, CardHealthBand, CardQualification, CardTemperatureEnum, CardUrgency, TaskPriorityEnum } from "../../public-api";
// Concrete paths, not the barrel: `public-api` re-exports this file, and resolving
// a newly added export back through that cycle fails to compile in the app build.
import { CardInteractionDirection, CardScoreBand } from "../models/card.model";
import { CardDiscardReasonEnum } from "../models/enum/card-discard-reason.enum";
import { LeadOriginTypeEnum } from "../models/enum/lead-origin-type.enum";
import { TaskOutcomeEnum } from "../models/enum/task-outcome.enum";
import { TaskTypeEnum } from "../models/enum/task-type.enum";

export type FilterType = 'select' | 'multiselect' | 'text' | 'number' | 'daterange';

export interface FilterOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface FilterDefinition {
  key: string;
  label: string;
  type: FilterType;
  defaultValue: any;
  placeholder?: string;
  options?: FilterOption[];
  /**
   * i18n key of the section this filter belongs to in the filter panel.
   *
   * The board already declares its filters in two batches — the deal's own fields and
   * the daily radar — and then concatenates them, so the panel had no way to tell a
   * flat list of fifteen rows apart. Optional: a definition without it falls into the
   * first section.
   */
  group?: string;
  /**
   * A filter the bar never offers in its "add filter" menu, but still shows as a chip
   * while it carries a value — the out-of-profile toggle lives on a button of its own.
   */
  hidden?: boolean;
}

export interface ActiveFilterChip {
  key: string;
  label: string;
  displayValue: string;
}


export const PRIORITY_LABEL: Record<TaskPriorityEnum, string> = {
  [TaskPriorityEnum.NONE]: 'task.enum.priorityNone',
  [TaskPriorityEnum.LOW]: 'task.enum.priorityLow',
  [TaskPriorityEnum.MEDIUM]: 'task.enum.priorityMedium',
  [TaskPriorityEnum.HIGH]: 'task.enum.priorityHigh',
  [TaskPriorityEnum.URGENT]: 'task.enum.priorityUrgent',
  [TaskPriorityEnum.CRITICAL]: 'task.enum.priorityCritical',
};

export const TEMPERATURE_LABEL: Record<CardTemperatureEnum, string> = {
  [CardTemperatureEnum.COLD]: 'kanban.temperature.cold',
  [CardTemperatureEnum.WARM]: 'kanban.temperature.warm',
  [CardTemperatureEnum.HOT]: 'kanban.temperature.hot',
};

/**
 * Daily-radar label maps (SPEC-2026-08-crm-pipeline-deals-radar).
 *
 * Every map stores an i18n KEY, never a literal: the value is piped through
 * translate at the render site so the four locales stay in step (RNF-07). A map
 * returning "Vencida" here would be invisible to the catalogs.
 */
/**
 * Declared in DESCENDING urgency, because the board's filter derives its options from
 * `Object.entries` of this map (see `KanbanComponent.radarFilterDefs`) and a filter
 * list reads best in the order a salesperson triages: what was missed, what is due
 * today, what has no plan at all, what is already handled.
 *
 * `NO_NEXT_ACTION` is NOT a second name for overdue — it is the absence of a
 * commitment, and it was folded into `OVERDUE` until concluding a task on its own due
 * date started reading as "Vencida" (owner report 2026-08-21).
 */
export const URGENCY_LABEL: Record<CardUrgency, string> = {
  OVERDUE: 'kanban.urgency.overdue',
  DUE_TODAY: 'kanban.urgency.dueToday',
  NO_NEXT_ACTION: 'kanban.urgency.noNextAction',
  SCHEDULED: 'kanban.urgency.scheduled',
};

/**
 * Urgency must be legible WITHOUT colour perception (RNF-08, AC-04): each state
 * carries its own icon and its own text label, and colour only reinforces them.
 *
 * `NO_NEXT_ACTION` deliberately mirrors `SCHEDULED`'s calendar — close against check,
 * an absent commitment against a present one — instead of borrowing the alarm, which
 * is the state it was just separated from.
 */
export const URGENCY_ICON: Record<CardUrgency, string> = {
  OVERDUE: 'ri-alarm-warning-line',
  DUE_TODAY: 'ri-time-line',
  NO_NEXT_ACTION: 'ri-calendar-close-line',
  SCHEDULED: 'ri-calendar-check-line',
};

/**
 * `NO_NEXT_ACTION` is muted, not red: nothing was promised, so nothing was broken.
 * The card already pairs it with the "schedule next action" link (RF-12), and
 * stagnation escalates it after 7 quiet days — that ladder is what carries the
 * severity, not a second red edge indistinguishable from a missed commitment.
 */
export const URGENCY_CONTEXT: Record<CardUrgency, string> = {
  OVERDUE: 'danger',
  DUE_TODAY: 'warning',
  NO_NEXT_ACTION: 'secondary',
  SCHEDULED: 'success',
};

export const QUALIFICATION_LABEL: Record<CardQualification, string> = {
  UNQUALIFIED: 'kanban.qualification.unqualified',
  MARKETING_QUALIFIED: 'kanban.qualification.marketingQualified',
  SALES_QUALIFIED: 'kanban.qualification.salesQualified',
};

export const QUALIFICATION_CONTEXT: Record<CardQualification, string> = {
  UNQUALIFIED: 'secondary',
  MARKETING_QUALIFIED: 'info',
  SALES_QUALIFIED: 'primary',
};

/** E3 RF-37: colour of the score badge. `NEUTRAL` is muted on purpose — it is not a warning. */
export const SCORE_BAND_CONTEXT: Record<CardScoreBand, string> = {
  STRONG: 'success',
  MODERATE: 'warning',
  NEUTRAL: 'secondary',
};

/**
 * The six current bands, strongest first — the order filters and the settings screen
 * show them in (E3 RF-11). `STABLE` is deliberately absent: it is the E1 legacy value
 * nothing produces any more, kept in the maps below only so unrecomputed deals still
 * render (ADR-003).
 */
export const HEALTH_BANDS: ReadonlyArray<CardHealthBand> = ['NEW', 'ENGAGED', 'HEALTHY', 'ATTENTION', 'COOLING', 'CRITICAL'];

export const HEALTH_BAND_LABEL: Record<CardHealthBand, string> = {
  NEW: 'kanban.healthBand.new',
  ENGAGED: 'kanban.healthBand.engaged',
  HEALTHY: 'kanban.healthBand.healthy',
  ATTENTION: 'kanban.healthBand.attention',
  COOLING: 'kanban.healthBand.cooling',
  CRITICAL: 'kanban.healthBand.critical',
  STABLE: 'kanban.healthBand.healthy',
};

export const HEALTH_BAND_CONTEXT: Record<CardHealthBand, string> = {
  NEW: 'secondary',
  ENGAGED: 'success',
  HEALTHY: 'success',
  ATTENTION: 'warning',
  COOLING: 'warning',
  CRITICAL: 'danger',
  STABLE: 'success',
};

/** E3 RF-14: a health reason is a criterion key — labelled like the settings screen — or one of two states. */
export function healthReasonLabelKey(reason?: string): string | null {
  return reason ? `kanban.scoreCriterion.${reason}` : null;
}

/** E3 RF-07: the four qualification facts, in the order the checklist shows them. */
export const BANT_FACTS = ['need', 'budget', 'decisionMaker', 'timeframe'] as const;
export type BantFact = (typeof BANT_FACTS)[number];

export const BANT_FACT_LABEL: Record<BantFact, string> = {
  need: 'kanban.bant.need',
  budget: 'kanban.bant.budget',
  decisionMaker: 'kanban.bant.decisionMaker',
  timeframe: 'kanban.bant.timeframe',
};

/** E3 RF-24 (ADR-001): labels of the platform discard reasons, keyed by the `_id` the list endpoint offers. */
export const DISCARD_REASON_LABEL: Record<CardDiscardReasonEnum, string> = {
  [CardDiscardReasonEnum.DISQUALIFIED_MQL]: 'kanban.discardReason.disqualifiedMql',
  [CardDiscardReasonEnum.DISQUALIFIED_SQL]: 'kanban.discardReason.disqualifiedSql',
  [CardDiscardReasonEnum.BLACKLIST]: 'kanban.discardReason.blacklist',
};

/** A loss reason offered by the platform rather than created by the company. */
export function isPlatformLossReason(reason?: { _id?: string; platform?: boolean } | null): boolean {
  return reason?.platform === true || Object.values(CardDiscardReasonEnum).includes(reason?._id as CardDiscardReasonEnum);
}

export const LEAD_ORIGIN_TYPE_LABEL: Record<LeadOriginTypeEnum, string> = {
  PAID_SOCIAL: 'kanban.leadOriginType.PAID_SOCIAL',
  ORGANIC_SOCIAL: 'kanban.leadOriginType.ORGANIC_SOCIAL',
  SEARCH: 'kanban.leadOriginType.SEARCH',
  INBOUND_MESSAGING: 'kanban.leadOriginType.INBOUND_MESSAGING',
  IMPORT: 'kanban.leadOriginType.IMPORT',
  WEBHOOK: 'kanban.leadOriginType.WEBHOOK',
  OTHER: 'kanban.leadOriginType.OTHER',
};

export const TASK_OUTCOME_LABEL: Record<TaskOutcomeEnum, string> = {
  HELD: 'kanban.taskOutcome.held',
  NO_ANSWER: 'kanban.taskOutcome.noAnswer',
  RESCHEDULED: 'kanban.taskOutcome.rescheduled',
  PROPOSAL_SENT: 'kanban.taskOutcome.proposalSent',
  NOT_INTERESTED: 'kanban.taskOutcome.notInterested',
  OTHER: 'kanban.taskOutcome.other',
};

/**
 * i18n key for an activity type — `FOLLOW_UP` -> `task.enum.typeFollowUp`.
 *
 * DERIVED rather than looked up in a map so the four screens that show an activity
 * type (the deal card's radar, the deal list, the deal detail's activity log and the
 * outcome dialog) cannot drift from each other: they each carried their own copy of
 * this reduce. No type at all is the radar's "unknown interaction", which is what all
 * four already answered.
 */
export function taskTypeLabelKey(type?: string): string {
  if (!type) return 'kanban.radar.interactionUnknown';

  const pascal = `${type}`
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

  return `task.enum.type${pascal}`;
}

/**
 * The seven activity types a user is offered wherever a sales activity is scheduled or
 * concluded (SPEC-2026-10-crm-activities-cadence RF-01), in the order the forms list them.
 * Defined ONCE: the next-action dialog, the task form and the conclusion dialog all read it.
 * An older type an activity already carries stays valid and is added beside these when that
 * activity is edited (RF-02) — never offered for a new one.
 */
export const SALES_ACTIVITY_TYPES: ReadonlyArray<TaskTypeEnum> = [
  TaskTypeEnum.CALL,
  TaskTypeEnum.WHATSAPP,
  TaskTypeEnum.EMAIL,
  TaskTypeEnum.FOLLOW_UP,
  TaskTypeEnum.MEETING,
  TaskTypeEnum.TASK,
  TaskTypeEnum.OTHERS,
];

/**
 * One icon per activity type, legible beside the label (E4 RNF-08). Material Design Icons,
 * because the activity rows and menus of the tasks area are MDI throughout.
 */
export const TASK_TYPE_ICON: Partial<Record<TaskTypeEnum, string>> = {
  [TaskTypeEnum.CALL]: 'mdi-phone-outline',
  [TaskTypeEnum.WHATSAPP]: 'mdi-whatsapp',
  [TaskTypeEnum.EMAIL]: 'mdi-email-outline',
  [TaskTypeEnum.FOLLOW_UP]: 'mdi-message-reply-text-outline',
  [TaskTypeEnum.MEETING]: 'mdi-account-group-outline',
  [TaskTypeEnum.PRESENTATION]: 'mdi-presentation',
  [TaskTypeEnum.APPOINTMENT]: 'mdi-calendar-clock',
  [TaskTypeEnum.VISIT]: 'mdi-map-marker-outline',
  [TaskTypeEnum.TASK]: 'mdi-checkbox-marked-circle-outline',
  [TaskTypeEnum.NOTE]: 'mdi-note-text-outline',
  [TaskTypeEnum.TRAINING]: 'mdi-school-outline',
  [TaskTypeEnum.FEEDBACK_COLLECTION]: 'mdi-comment-quote-outline',
  [TaskTypeEnum.OTHERS]: 'mdi-dots-horizontal-circle-outline',
};

/**
 * i18n key for the stagnation tooltip, chosen by WHO spoke last (RF-15).
 *
 * The badge says the deal is cold and unattended; what to do about it depends on the
 * direction. A lead that stopped answering needs reheating; a lead who wrote and got
 * no reply is a loss by neglect, and that one is ours. Shared by the board card and
 * the deal list so the two views cannot tell different stories about the same deal.
 *
 * An absent direction falls back to the neutral copy: deals whose last interaction
 * predates the field keep working (RNF-09).
 */
export function stagnantTooltipKey(direction?: CardInteractionDirection): string {
  switch (direction) {
    case 'INBOUND':
      return 'kanban.radar.stagnantTooltipInbound';
    case 'OUTBOUND':
      return 'kanban.radar.stagnantTooltipOutbound';
    default:
      return 'kanban.radar.stagnantTooltip';
  }
}

/** Age bands of the funnel overview (RF-21). Labels only — the API names the band. */
export const AGE_BAND_LABEL: Record<string, string> = {
  '0-7': 'kanban.ageBand.upTo7',
  '8-15': 'kanban.ageBand.upTo15',
  '16-30': 'kanban.ageBand.upTo30',
  '31-60': 'kanban.ageBand.upTo60',
  '61+': 'kanban.ageBand.over60',
};

export interface StageUpdatePayload {
  card: Card;
  targetStageId: string;
  previousStageId: string;
  previousContainerData: Card[];
  targetContainerData: Card[];
}
